import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, Cloud, HardDrive, Settings, Play, RefreshCw, Download,
  CheckCircle2, XCircle, AlertTriangle, Trash2, FileArchive, Clock,
  Lock, KeyRound, History as HistoryIcon, BookOpen, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  if (n > 1024 * 1024 * 1024) return (n / 1024 / 1024 / 1024).toFixed(2) + " GB";
  if (n > 1024 * 1024) return (n / 1024 / 1024).toFixed(2) + " MB";
  if (n > 1024) return (n / 1024).toFixed(1) + " KB";
  return n + " B";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES"); } catch { return iso; }
};

export default function ManagedBackupsPage() {
  const { axiosAuth, user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [restoreFor, setRestoreFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const load = useCallback(async () => {
    try {
      const [st, lst, cfg, pf] = await Promise.all([
        axiosAuth().get("/admin/backups/managed/status"),
        axiosAuth().get("/admin/backups/managed/list?limit=50"),
        axiosAuth().get("/admin/backups/managed/settings"),
        axiosAuth().get("/admin/backups/managed/preflight").catch(() => ({ data: null })),
      ]);
      setStatus(st.data);
      setHistory(lst.data.items || []);
      setSettings(cfg.data);
      setPreflight(pf.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando backups");
    } finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const handleRun = async () => {
    // Pre-validación: si destino M365 y preflight dice que falta algo, bloquear
    // con mensaje claro antes de iniciar (evita copias huérfanas).
    if (settings?.destination === "microsoft365" && preflight && !preflight.destination?.ok) {
      const err = preflight.destination?.error || "Microsoft 365 no está configurado correctamente.";
      toast.error(`No se puede crear backup: ${err}. Abre Configuración y revisa las credenciales.`, { duration: 6000 });
      return;
    }
    if (preflight && !preflight.mongodump?.available) {
      toast.error("mongodump no está disponible. Instala MongoDB Database Tools.");
      return;
    }
    if (!window.confirm("Crear un backup completo ahora? Tardará unos segundos.")) return;
    setRunning(true);
    try {
      const res = await axiosAuth().post("/admin/backups/managed/run");
      if (res.data?.status === "partial") {
        toast.warning(`Backup PARCIAL: copia local creada (${res.data.file_name}) pero la subida a Microsoft 365 falló: ${res.data.remote_error || "ver historial"}`, { duration: 8000 });
      } else {
        toast.success(`Backup creado: ${res.data.file_name}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando backup");
    } finally { setRunning(false); }
  };

  const handleValidate = async (id) => {
    try {
      const r = await axiosAuth().post(`/admin/backups/managed/validate/${id}`);
      if (r.data.valid) {
        toast.success("Backup válido. Checks: " + JSON.stringify(r.data.checks));
      } else {
        toast.error("Backup NO válido. " + JSON.stringify(r.data.checks));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error validando");
    }
  };

  const handleDownload = async (h) => {
    try {
      const res = await axiosAuth().get(`/admin/backups/managed/download/${h.id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = h.file_name || "backup.tar.gz"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error descargando");
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "ELIMINAR") { toast.error("Escribe ELIMINAR"); return; }
    try {
      await axiosAuth().delete(`/admin/backups/managed/${deleteFor.id}?confirm=ELIMINAR`);
      toast.success("Backup borrado");
      setDeleteFor(null); setConfirmText(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error borrando");
    }
  };

  const handleRestore = async () => {
    if (confirmText !== "RESTAURAR BETA") {
      toast.error("Escribe RESTAURAR BETA"); return;
    }
    try {
      const r = await axiosAuth().post(`/admin/backups/managed/restore/${restoreFor.id}`, {
        confirm_text: "RESTAURAR BETA",
      });
      toast.info(r.data.message);
      setRestoreFor(null); setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (loading) return <p className="p-6 text-sm text-slate-400">Cargando…</p>;

  const last = status?.last_backup;
  const statusBadge = last ? (last.status === "ok" ? (
    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 rounded-sm"><CheckCircle2 className="w-3 h-3 mr-1" /> CORRECTO</Badge>
  ) : last.status === "partial" ? (
    <Badge className="bg-amber-100 text-amber-700 border-amber-200 rounded-sm" data-testid="bm-last-partial"><AlertTriangle className="w-3 h-3 mr-1" /> PARCIAL · LOCAL OK</Badge>
  ) : last.status === "error" ? (
    <Badge className="bg-red-100 text-red-700 border-red-200 rounded-sm"><XCircle className="w-3 h-3 mr-1" /> ERROR</Badge>
  ) : (
    <Badge className="bg-amber-100 text-amber-700 border-amber-200 rounded-sm"><Clock className="w-3 h-3 mr-1" /> EN CURSO</Badge>
  )) : <Badge className="bg-slate-100 text-slate-500">SIN BACKUPS</Badge>;

  return (
    <div className="space-y-4" data-testid="managed-backups-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">Copias de Seguridad</h1>
          <p className="text-sm text-slate-500 mt-1">
            Backups automáticos diarios con cifrado AES-256 y destino externo Microsoft 365 (SharePoint/OneDrive) o almacenamiento local.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setGuideOpen(true)} className="rounded-sm text-xs uppercase tracking-wider" data-testid="bm-guide-btn">
            <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Guía Microsoft 365
          </Button>
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => setSettingsOpen(true)} className="rounded-sm text-xs uppercase tracking-wider" data-testid="bm-settings-btn">
              <Settings className="w-3.5 h-3.5 mr-1.5" /> Configuración
            </Button>
          )}
          <Button onClick={load} variant="ghost" size="sm" className="rounded-sm"><RefreshCw className="w-3.5 h-3.5" /></Button>
          {isSuperAdmin && (
            <Button onClick={handleRun} disabled={running} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm text-xs uppercase tracking-wider" data-testid="bm-run-btn">
              <Play className="w-3.5 h-3.5 mr-1.5" /> {running ? "Creando…" : "Crear copia ahora"}
            </Button>
          )}
        </div>
      </div>

      {/* Estado último backup */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Estado último backup</p>
              <div className="mt-1">{statusBadge}</div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Fecha</p>
              <p className="text-sm font-semibold text-slate-900 mt-1" data-testid="bm-last-date">{fmtDate(last?.started_at)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Tamaño</p>
              <p className="text-sm font-semibold text-slate-900 mt-1">{fmtBytes(last?.size_bytes)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Destino</p>
              <p className="text-sm font-semibold text-slate-900 mt-1 flex items-center gap-1">
                {settings?.destination === "microsoft365" ? (<><Cloud className="w-3 h-3 text-blue-600" /> Microsoft 365</>) : (<><HardDrive className="w-3 h-3 text-slate-600" /> Local VPS</>)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Retención</p>
              <p className="text-sm font-semibold text-slate-900 mt-1">{settings?.retention_days || 30} días</p>
            </div>
          </div>
          {last?.file_name && (
            <p className="text-[11px] text-slate-500 font-mono mt-3 break-all">
              📄 {last.file_name}
              {last.checksum_sha256 && <span className="ml-2 text-slate-400">SHA256: {last.checksum_sha256.slice(0, 16)}…</span>}
            </p>
          )}
          {last?.error && (
            <p className="text-xs text-red-700 mt-2 bg-red-50 p-2 rounded-sm border border-red-200" data-testid="bm-last-error">
              <AlertTriangle className="w-3 h-3 inline mr-1" /> {last.error}
            </p>
          )}
          {last?.remote_error && (
            <p className="text-xs text-amber-800 mt-2 bg-amber-50 p-2 rounded-sm border border-amber-200">
              <Cloud className="w-3 h-3 inline mr-1" /> Subida M365 fallida: {last.remote_error}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Configuración resumen */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-slate-500" />
              Programación: <strong>{settings?.auto_enabled ? `Diaria a ${String(settings.auto_hour).padStart(2,"0")}:${String(settings.auto_minute).padStart(2,"0")} UTC` : "Desactivada"}</strong>
            </span>
            <span className="flex items-center gap-1">
              <Lock className="w-3 h-3 text-slate-500" />
              Cifrado: <strong>{settings?.encryption_enabled && settings?.encryption_key_set ? "AES (Fernet) activo" : "Desactivado"}</strong>
            </span>
            {settings?.destination === "microsoft365" && (
              <span className="flex items-center gap-1">
                <Cloud className="w-3 h-3 text-blue-500" />
                Carpeta: <strong>{settings?.microsoft?.folder_path || "—"}</strong>
              </span>
            )}
            <span className="ml-auto text-slate-400">Total: {status?.totals?.all || 0} · OK: {status?.totals?.ok || 0} · Parcial: {status?.totals?.partial || 0} · Err: {status?.totals?.error || 0}</span>
          </div>
        </CardContent>
      </Card>

      {/* Comprobaciones técnicas (preflight) */}
      {preflight && (
        <Card className="rounded-sm border-slate-200" data-testid="bm-preflight-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Comprobaciones técnicas</p>
              <Badge className={preflight.ready_for_backup
                ? "bg-emerald-100 text-emerald-700 border-emerald-200 rounded-sm"
                : "bg-red-100 text-red-700 border-red-200 rounded-sm"}
                data-testid="bm-preflight-overall">
                {preflight.ready_for_backup ? "LISTO PARA BACKUP" : "REQUIERE ATENCIÓN"}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
              <HealthRow
                testid="bm-pre-mongodump"
                ok={preflight.mongodump?.available}
                label="MongoDB dump"
                value={preflight.mongodump?.label}
                hint={preflight.mongodump?.hint}
              />
              <HealthRow
                testid="bm-pre-destination"
                ok={preflight.destination?.ok}
                label="Destino externo"
                value={preflight.destination?.label}
                hint={preflight.destination?.error}
              />
              <HealthRow
                testid="bm-pre-encryption"
                ok={preflight.encryption?.ok}
                label="Cifrado"
                value={preflight.encryption?.label}
                hint={preflight.encryption?.enabled && !preflight.encryption?.key_set
                  ? "Define la clave en Configuración → Cifrado AES-256"
                  : null}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historial */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {history.length === 0 ? (
            <div className="p-12 text-center" data-testid="bm-empty">
              <FileArchive className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Aún no hay backups. Pulsa "Crear copia ahora" para empezar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Tipo</th>
                    <th className="p-2 text-left">Estado</th>
                    <th className="p-2 text-left">Tamaño</th>
                    <th className="p-2 text-left">Destino</th>
                    <th className="p-2 text-left">Duración</th>
                    <th className="p-2 text-left">Usuario</th>
                    <th className="p-2 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map(h => (
                    <tr key={h.id} data-testid={`bm-row-${h.id}`}>
                      <td className="p-2 whitespace-nowrap">{fmtDate(h.started_at)}</td>
                      <td className="p-2">
                        <Badge className={h.triggered_by === "scheduled" ? "bg-blue-100 text-blue-700 border-blue-200 rounded-sm" : "bg-slate-100 text-slate-700 rounded-sm"}>
                          {h.triggered_by === "scheduled" ? "Auto" : "Manual"}
                        </Badge>
                      </td>
                      <td className="p-2">
                        {h.status === "ok" ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 rounded-sm">OK</Badge> :
                          h.status === "partial" ? <Badge className="bg-amber-100 text-amber-700 border-amber-200 rounded-sm" title={h.remote_error || "Local OK, M365 falló"}>PARCIAL</Badge> :
                          h.status === "error" ? <Badge className="bg-red-100 text-red-700 border-red-200 rounded-sm">ERROR</Badge> :
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 rounded-sm">{h.status}</Badge>}
                      </td>
                      <td className="p-2">{fmtBytes(h.size_bytes)}</td>
                      <td className="p-2">
                        {h.destination === "microsoft365" ? <Cloud className="w-3 h-3 inline mr-0.5 text-blue-600" /> : <HardDrive className="w-3 h-3 inline mr-0.5 text-slate-600" />}
                        {h.destination === "microsoft365" ? (h.remote_id ? "M365" : "M365 (solo local)") : "Local"}
                      </td>
                      <td className="p-2">{h.duration_seconds ? `${h.duration_seconds.toFixed(1)}s` : "—"}</td>
                      <td className="p-2 text-[10px] text-slate-500">{h.actor_email || (h.triggered_by === "scheduled" ? "sistema" : "—")}</td>
                      <td className="p-2 flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleValidate(h.id)} className="text-[10px] uppercase h-7 rounded-sm" data-testid={`bm-validate-${h.id}`}>
                          <ShieldCheck className="w-3 h-3" />
                        </Button>
                        {isSuperAdmin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleDownload(h)} className="text-[10px] uppercase h-7 rounded-sm" data-testid={`bm-download-${h.id}`}>
                              <Download className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setRestoreFor(h)} className="text-[10px] uppercase h-7 rounded-sm text-amber-700" data-testid={`bm-restore-${h.id}`}>
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteFor(h)} className="text-[10px] uppercase h-7 rounded-sm text-red-600" data-testid={`bm-delete-${h.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings dialog */}
      {isSuperAdmin && settings && (
        <SettingsDialog
          open={settingsOpen} onClose={() => setSettingsOpen(false)}
          current={settings} axiosAuth={axiosAuth} onSaved={load}
        />
      )}

      {/* Guide dialog */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-sm" data-testid="bm-guide-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">Guía: Conectar Microsoft 365</DialogTitle>
            <DialogDescription>Sigue estos pasos en tu Microsoft Entra admin center.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-slate-700">
            <Step n={1} title="Entra en Microsoft Entra admin center" body="https://entra.microsoft.com → Aplicaciones → Registros de aplicaciones." />
            <Step n={2} title="Crea una App Registration" body="Botón «Nuevo registro». Nombre sugerido: ASCAUTO-Backups. Tipos de cuenta admitidos: solo este directorio organizativo. Sin redirect URI." />
            <Step n={3} title="Anota Tenant ID y Client ID" body="Aparecen en la pestaña «Información general» del registro recién creado." />
            <Step n={4} title="Crea un Client Secret" body='Pestaña «Certificados y secretos» → «Nuevo secreto de cliente». Caducidad recomendada: 24 meses. COPIA el VALOR (no el ID) — sólo se muestra una vez.' />
            <Step n={5} title="Concede permisos de Microsoft Graph" body="Pestaña «Permisos de API» → «Agregar un permiso» → Microsoft Graph → «Permisos de aplicación»:" extra={
              <ul className="list-disc ml-5 text-xs text-slate-600 mt-1">
                <li><code>Sites.ReadWrite.All</code> — necesario para SharePoint (advertencia: permiso amplio).</li>
                <li className="text-slate-400">Alternativa fina: <code>Sites.Selected</code> + grant por sitio (v2 futura).</li>
              </ul>
            } />
            <Step n={6} title="Conceder consentimiento del administrador" body='Pestaña «Permisos de API» → botón «Conceder consentimiento de administrador para [tu tenant]».' />
            <Step n={7} title="Crea la carpeta destino en SharePoint" body="Ve a tu sitio SharePoint (p.ej. https://tutenant.sharepoint.com/sites/ASCAUTO) → Documentos → Crea carpeta ASCAUTO/Backups/beta." />
            <Step n={8} title="Pega los datos en Configuración" body="Aquí en el panel: pulsa «Configuración» y rellena tenant_id, client_id, client_secret, site_url (https://tutenant.sharepoint.com/sites/ASCAUTO) y folder_path (ASCAUTO/Backups/beta)." />
            <Step n={9} title="Prueba la conexión" body='Botón «Probar conexión Microsoft 365». Si responde OK, ya está listo.' />
            <Step n={10} title="Crea un backup manual" body='Pulsa «Crear copia ahora» y verifica que el archivo aparece en SharePoint.' />
          </ol>
        </DialogContent>
      </Dialog>

      {/* Restore dialog */}
      <Dialog open={!!restoreFor} onOpenChange={() => { setRestoreFor(null); setConfirmText(""); }}>
        <DialogContent className="max-w-md rounded-sm border-amber-300" data-testid="bm-restore-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase text-amber-800">Restaurar backup</DialogTitle>
            <DialogDescription className="space-y-1">
              <span className="block">Entiendo que restaurar un backup sobrescribirá los datos actuales.</span>
              <span className="block font-mono text-[10px]">{restoreFor?.file_name}</span>
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Escribe exactamente <span className="font-mono bg-slate-100 px-1.5 py-0.5">RESTAURAR BETA</span></Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="rounded-sm mt-1" data-testid="bm-restore-text" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestoreFor(null); setConfirmText(""); }} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleRestore} disabled={confirmText !== "RESTAURAR BETA"} className="bg-amber-700 text-white rounded-sm text-xs uppercase disabled:bg-slate-300" data-testid="bm-restore-confirm">
              Restaurar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteFor} onOpenChange={() => { setDeleteFor(null); setConfirmText(""); }}>
        <DialogContent className="max-w-md rounded-sm border-red-300" data-testid="bm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase text-red-700">Borrar backup</DialogTitle>
            <DialogDescription>Vas a borrar <span className="font-mono text-[10px]">{deleteFor?.file_name}</span> local y en M365.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Escribe <span className="font-mono bg-slate-100 px-1.5 py-0.5">ELIMINAR</span></Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="rounded-sm mt-1" data-testid="bm-delete-text" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteFor(null); setConfirmText(""); }} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleDelete} disabled={confirmText !== "ELIMINAR"} className="bg-red-700 text-white rounded-sm text-xs uppercase disabled:bg-slate-300" data-testid="bm-delete-confirm">
              Borrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Step({ n, title, body, extra }) {
  return (
    <li className="flex gap-3 border-l-2 border-slate-200 pl-3">
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mt-0.5">{String(n).padStart(2, "0")}</span>
      <div className="flex-1">
        <p className="font-semibold text-slate-800 text-xs">{title}</p>
        <p className="text-xs text-slate-600 mt-0.5">{body}</p>
        {extra}
      </div>
    </li>
  );
}


function SettingsDialog({ open, onClose, current, axiosAuth, onSaved }) {
  const [form, setForm] = useState({
    destination: current.destination || "local",
    auto_enabled: !!current.auto_enabled,
    auto_hour: current.auto_hour ?? 2,
    auto_minute: current.auto_minute ?? 0,
    retention_days: current.retention_days || 30,
    encryption_enabled: !!current.encryption_enabled,
    encryption_key: "",
    microsoft: {
      tenant_id: current.microsoft?.tenant_id || "",
      client_id: current.microsoft?.client_id || "",
      client_secret: "",
      drive_target: current.microsoft?.drive_target || "sharepoint",
      site_url: current.microsoft?.site_url || "",
      drive_id: current.microsoft?.drive_id || "",
      site_id: current.microsoft?.site_id || "",
      folder_path: current.microsoft?.folder_path || "ASCAUTO/Backups/beta",
    },
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const secretSet = current.microsoft?.client_secret_set;
  const keySet = current.encryption_key_set;

  const setMs = (k, v) => setForm(p => ({ ...p, microsoft: { ...p.microsoft, [k]: v } }));

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await axiosAuth().post("/admin/backups/managed/microsoft/test", form.microsoft);
      setTestResult({ ok: true, ...r.data });
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Error" });
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Construimos un payload nuevo sin mutar `form` ni enviar secretos vacíos
      // que pudieran borrar los guardados en BD (defensa en profundidad).
      const ms = { ...form.microsoft };
      // Solo enviamos client_secret si el usuario ha tecleado algo no vacío.
      if (!ms.client_secret || !ms.client_secret.trim()) {
        delete ms.client_secret;
      }
      const payload = {
        destination: form.destination,
        auto_enabled: form.auto_enabled,
        auto_hour: form.auto_hour,
        auto_minute: form.auto_minute,
        retention_days: form.retention_days,
        encryption_enabled: form.encryption_enabled,
        microsoft: ms,
      };
      if (form.encryption_key && form.encryption_key.trim()) {
        payload.encryption_key = form.encryption_key;
      }
      await axiosAuth().put("/admin/backups/managed/settings", payload);
      toast.success("Configuración guardada");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const handleClearSecret = async () => {
    if (!window.confirm("¿Borrar el client_secret de Microsoft 365 guardado? Esto desconectará el destino externo hasta que pegues uno nuevo.")) return;
    setSaving(true);
    try {
      await axiosAuth().put("/admin/backups/managed/settings", {
        microsoft: { client_secret: "__CLEAR__" },
      });
      toast.success("Client secret borrado");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error borrando secreto");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-sm" data-testid="bm-settings-dialog">
        <DialogHeader>
          <DialogTitle className="heading-industrial text-lg uppercase">Configuración de backups</DialogTitle>
          <DialogDescription>Solo super_admin. Los secretos no se muestran tras guardar.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Destino */}
          <Section title="Destino externo">
            <div className="grid grid-cols-3 gap-2">
              {["local", "microsoft365"].map(d => (
                <button key={d} type="button" onClick={() => setForm(p => ({...p, destination: d}))}
                        className={`p-3 rounded-sm border text-xs uppercase tracking-wider ${form.destination===d ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}
                        data-testid={`bm-dest-${d}`}>
                  {d === "local" ? <><HardDrive className="w-4 h-4 mx-auto mb-1" /> Local VPS</> : <><Cloud className="w-4 h-4 mx-auto mb-1" /> Microsoft 365</>}
                </button>
              ))}
            </div>
          </Section>

          {/* Programación */}
          <Section title="Programación automática">
            <div className="flex items-center gap-3">
              <Switch checked={form.auto_enabled} onCheckedChange={(v) => setForm(p => ({...p, auto_enabled: v}))} data-testid="bm-auto-switch" />
              <span className="text-sm">{form.auto_enabled ? "Activada" : "Desactivada"} — diaria a las</span>
              <Input type="number" min={0} max={23} value={form.auto_hour} onChange={(e) => setForm(p => ({...p, auto_hour: parseInt(e.target.value)||0}))} className="w-16 h-8 rounded-sm" data-testid="bm-auto-hour" />
              <span>:</span>
              <Input type="number" min={0} max={59} value={form.auto_minute} onChange={(e) => setForm(p => ({...p, auto_minute: parseInt(e.target.value)||0}))} className="w-16 h-8 rounded-sm" data-testid="bm-auto-minute" />
              <span className="text-xs text-slate-500">UTC</span>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <Label className="text-xs">Retención (días):</Label>
              <Input type="number" min={1} max={365} value={form.retention_days} onChange={(e) => setForm(p => ({...p, retention_days: parseInt(e.target.value)||30}))} className="w-20 h-8 rounded-sm" data-testid="bm-retention" />
            </div>
          </Section>

          {/* Cifrado */}
          <Section title="Cifrado AES-256">
            <div className="flex items-center gap-3 mb-2">
              <Switch checked={form.encryption_enabled} onCheckedChange={(v) => setForm(p => ({...p, encryption_enabled: v}))} data-testid="bm-enc-switch" />
              <span className="text-sm">Cifrar archivos con passphrase</span>
            </div>
            {form.encryption_enabled && (
              <>
                <Label className="text-xs">{keySet ? "Cambiar passphrase (dejar vacío para mantener la actual)" : "Define una passphrase fuerte"}</Label>
                <Input type="password" value={form.encryption_key}
                        onChange={(e) => setForm(p => ({...p, encryption_key: e.target.value}))}
                        placeholder={keySet ? "•••••••••• (guardada)" : "Mínimo 16 caracteres"}
                        className="rounded-sm h-9 mt-1" data-testid="bm-enc-key" />
                <p className="text-[10px] text-red-700 mt-1 bg-red-50 p-2 border border-red-200 rounded-sm">
                  ⚠️ Si pierdes esta clave, los backups cifrados NO se podrán restaurar.
                </p>
              </>
            )}
          </Section>

          {/* Microsoft 365 */}
          {form.destination === "microsoft365" && (
            <Section title="Credenciales Microsoft 365">
              <div className="grid grid-cols-2 gap-2">
                <FieldM label="Tenant ID" value={form.microsoft.tenant_id} onChange={(v) => setMs("tenant_id", v)} testid="bm-tenant" />
                <FieldM label="Client ID" value={form.microsoft.client_id} onChange={(v) => setMs("client_id", v)} testid="bm-client" />
              </div>
              <div className="mt-2">
                <Label className="text-xs">Client secret {secretSet && <span className="text-emerald-700">✓ guardado</span>}</Label>
                <Input type="password" value={form.microsoft.client_secret}
                       onChange={(e) => setMs("client_secret", e.target.value)}
                       placeholder={secretSet ? "•••••••••• (dejar vacío para mantener)" : "Pega aquí el VALOR del secret"}
                       className="rounded-sm h-9 mt-1" data-testid="bm-secret" />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs">Tipo de destino</Label>
                  <Select value={form.microsoft.drive_target} onValueChange={(v) => setMs("drive_target", v)}>
                    <SelectTrigger className="rounded-sm h-9" data-testid="bm-drive-target"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sharepoint">SharePoint (recomendado)</SelectItem>
                      <SelectItem value="onedrive">OneDrive (no soporta app-only)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <FieldM label="Folder path (carpeta destino)" value={form.microsoft.folder_path} onChange={(v) => setMs("folder_path", v)} testid="bm-folder" />
              </div>
              <FieldM label="Site URL (https://tenant.sharepoint.com/sites/ASCAUTO)" value={form.microsoft.site_url} onChange={(v) => setMs("site_url", v)} testid="bm-site-url" />
              <div className="grid grid-cols-2 gap-2">
                <FieldM label="Site ID (opcional)" value={form.microsoft.site_id} onChange={(v) => setMs("site_id", v)} testid="bm-site-id" />
                <FieldM label="Drive ID (opcional)" value={form.microsoft.drive_id} onChange={(v) => setMs("drive_id", v)} testid="bm-drive-id" />
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button onClick={handleTest} disabled={testing} variant="outline" className="rounded-sm text-xs uppercase tracking-wider" data-testid="bm-test-ms-btn">
                  <KeyRound className="w-3.5 h-3.5 mr-1.5" /> {testing ? "Probando…" : "Probar conexión Microsoft 365"}
                </Button>
                {secretSet && (
                  <Button onClick={handleClearSecret} disabled={saving} variant="outline" className="rounded-sm text-xs uppercase tracking-wider border-red-200 text-red-700 hover:bg-red-50" data-testid="bm-clear-secret-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Borrar client_secret
                  </Button>
                )}
                {testResult?.ok && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 rounded-sm" data-testid="bm-test-ok">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> OK · {testResult.drive_name} · carpeta {testResult.folder_exists ? "existe" : "se creará al subir"}
                  </Badge>
                )}
                {testResult && !testResult.ok && (
                  <Badge className="bg-red-100 text-red-700 border-red-200 rounded-sm" data-testid="bm-test-fail">
                    <XCircle className="w-3 h-3 mr-1" /> {testResult.error?.slice(0, 80)}
                  </Badge>
                )}
              </div>
              <p className="text-[10px] text-amber-800 mt-2 bg-amber-50 p-2 border border-amber-200 rounded-sm">
                ⚠️ <strong>Permiso amplio</strong>: el registro Azure necesita <code className="font-mono">Sites.ReadWrite.All</code>. Una v2 futura soportará <code className="font-mono">Sites.Selected</code> (más restrictivo).
              </p>
            </Section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm text-xs uppercase">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 text-white rounded-sm text-xs uppercase" data-testid="bm-save-settings">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/50">
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">{title}</p>
      {children}
    </div>
  );
}

function HealthRow({ testid, ok, label, value, hint }) {
  return (
    <div className={`flex items-start gap-2 p-2 rounded-sm border ${ok ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/40"}`} data-testid={testid}>
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`text-xs font-semibold ${ok ? "text-slate-800" : "text-red-800"} truncate`} title={value || ""}>{value || "—"}</p>
        {hint && !ok && (
          <p className="text-[10px] text-red-700 mt-0.5 break-words">{hint}</p>
        )}
      </div>
    </div>
  );
}

function FieldM({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm h-9 mt-1" data-testid={testid} />
    </div>
  );
}
